import React from 'react';
import clsx from 'clsx';
import {
  FormControl, FormControlOwnProps, FormHelperText, FormHelperTextProps,
} from '../form';
import { NumberInput, NumberInputProps } from './NumberInput';
import { InputLabel, InputLabelProps } from '../inputLabel';
import useFormFieldId from '../utilities/useFormFieldId';

export interface NumberFieldProps extends
  NumberInputProps,
  FormControlOwnProps {
  /**
   * Children node for InputLabel component
   */
  label?: React.ReactNode;
  /**
   * Children node for FormHelperText component
   */
  helperText?: React.ReactNode;
  /**
   * Props to reach elements inside, extends OutlinedInput standard slots
   * and adds formControl, label, helperText
   */
  slotProps?: NumberInputProps['slotProps'] & {
    formControl?: Partial<FormControlOwnProps>;
    label?: Partial<InputLabelProps>;
    helperText?: Partial<FormHelperTextProps>;
  }
}

export const NumberField = React.forwardRef(
  (props: NumberFieldProps, ref: React.RefObject<unknown>) => {
    const {
      label, helperText, required, id: idOverride, sx,
      size, labelPosition, className, slotProps, ...inputProps
    } = props;

    const [id, helperTextId, inputLabelId] = useFormFieldId({
      idOverride, helperText, label,
    });

    return (
      <FormControl
        size={size}
        labelPosition={labelPosition}
        className={className}
        ref={ref as React.RefObject<HTMLDivElement>}
        {...slotProps?.formControl}
      >
        {label != null && label !== '' && (
          <InputLabel
            required={required}
            size={size}
            {...slotProps?.label}
            className={clsx(
              { [`${className}-label`]: className },
              slotProps?.label?.className,
            )}
            id={inputLabelId}
            htmlFor={id}
          >
            {label}
          </InputLabel>
        )}

        <NumberInput
          {...inputProps}
          size={size}
          id={id}
          slotProps={slotProps}
        />

        {helperText && (
          <FormHelperText
            {...slotProps?.helperText}
            id={helperTextId}
            className={clsx(
              slotProps?.helperText?.className,
              { [`${className}-helperText`]: className },
            )}
          >
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  },
);
